import React from "react";
import {
    CardBody,
    Card,
    GdRadio,
    Button,
    Menu,
    GdAutocomplete,
    GdCheckbox,
    Input,
    EditableList,
    Icon,
    Dropdown,
    DateTimeRangePicker, StandardListCell, CardHeader, StateOptions
} from "gd-react";
import GridDuck from 'gridduck';
import moment from "moment";
import history from "../../meta/history";
import './CreateDataDownload.scss'
import GenericLoader from "../../components/GenericLoader";
import OptionCard from "../../components/OptionCard/OptionCard";
import {createPortal} from "react-dom";

// mirrors the floor enforced by webhookDeliverySchema in gd-client-api
const MIN_WEBHOOK_SECRET_LENGTH = 16;

class CreateDataDownload extends React.Component {
    constructor(props) {
        super(props);

        // modal flow only; the report wizard renders the inPage branch, which never reads these
        this.state = this.props.ddState ? this.props.ddState : {
            step: 1,
            mode: null,
            scheduleName: '',
            scheduleType: 'schedule_weekly',
            emailEnabled: false,
            emailRecipients: '',
            webhookEnabled: false,
            webhookUrl: '',
            webhookSecretInput: '',
            webhookSecret: null,
            webhookSecretStored: false,
            sftpEnabled: false,
            sftpHost: '',
            sftpPort: '',
            sftpUsername: '',
            sftpRemotePath: '',
            sftpAuthMethod: 'password',
            sftpPassword: '',
            sftpPrivateKey: '',
            sftpPassphrase: '',
            sftpSecretStored: false,
            selectionType: 'individual',
            filterLogic: 'OR',
            dataType: 'DELIVERED',
            granularity: 60 * 60,
            customGranularity: 60,
            selectedAssets: [],
            filters: [],
            listSaved: false,
            isEditing: false,
            format: 'DD/MM/yyyy HH'
        };


        this.filteredAssetList = this.props.filteredAssetList ? this.props.filteredAssetList : [];
        this.selectionChanged = this.selectionChanged.bind(this);
        this.getSelection = this.getSelection.bind(this);
        this.getFilters = this.getFilters.bind(this);
        this.getList = this.getList.bind(this);
        this.filterUpdated = this.filterUpdated.bind(this);
        this.deleteFilter = this.deleteFilter.bind(this);
        this.updateSelectedAssets = this.updateSelectedAssets.bind(this);
        this.getIndividualList = this.getIndividualList.bind(this);
        this.getAvailableIndividualList = this.getAvailableIndividualList.bind(this);
        this.saveIndividualList = this.saveIndividualList.bind(this);
        this.setGranularity = this.setGranularity.bind(this);
        this.setCustomGranularity = this.setCustomGranularity.bind(this);
        this.rangeUpdated = this.rangeUpdated.bind(this);
        this.isValidDate = this.isValidDate.bind(this);
        this.canSubmit = this.canSubmit.bind(this);
        this.deliveryComplete = this.deliveryComplete.bind(this);
        this.webhookSecretRequired = this.webhookSecretRequired.bind(this);
        this.webhookSecretError = this.webhookSecretError.bind(this);
        this.sftpCredentialRequired = this.sftpCredentialRequired.bind(this);
        this.sftpCredentialError = this.sftpCredentialError.bind(this);
        this.notificationConfig = this.notificationConfig.bind(this);
        this.copySecret = this.copySecret.bind(this);
        this.setMode = this.setMode.bind(this);
        this.createDataRequest = this.createDataRequest.bind(this);
        this.createSchedule = this.createSchedule.bind(this);
        this.updateSchedule = this.updateSchedule.bind(this);
        this.submit = this.submit.bind(this);
    }

    setMode(mode) {
        // canSubmit only reads the active mode's fields, so the other mode's input is left alone
        this.setState({mode: mode});
    }

    copySecret() {
        let self = this;
        return navigator.clipboard.writeText(this.state.webhookSecret).then(function () {
            self.setState({secretCopied: true});
        });
    }

    submit() {
        if (this.props.editing) {
            return this.updateSchedule();
        }
        return this.state.mode === 'schedule' ? this.createSchedule() : this.createDataRequest();
    }

    // One PATCH carrying the whole selection; the API merges it into the schedule's parameters and
    // re-derives compression from the device count.
    updateSchedule() {
        let self = this;
        return this.props.editing.set({
            name: this.state.scheduleName,
            granularity: this.state.granularity !== 'custom' ? this.state.granularity : this.state.customGranularity,
            assetIds: this.state.selectedAssets.map(a => a.id),
            dataType: this.state.dataType,
            // {} rather than undefined: an omitted key would leave the stored config in place, so
            // turning both channels off could never be saved
            notificationConfig: this.notificationConfig() || {}
        }).then(function () {
            if (self.props.onsubmit) {
                self.props.onsubmit();
            }
        });
    }

    // omitted entirely when neither channel is on, so the API stores null rather than an empty shape
    notificationConfig() {
        let config = {};
        if (this.state.emailEnabled) {
            config.email = {
                recipients: this.state.emailRecipients.split(',').map(r => r.trim()).filter(r => r.length)
            };
        }
        if (this.state.webhookEnabled) {
            config.webhook = {url: this.state.webhookUrl.trim()};
            // omitted rather than sent empty, which is what makes the API generate one
            const secret = this.state.webhookSecretInput.trim();
            if (secret.length) {
                config.webhook.secret = secret;
            }
        }
        if (this.state.sftpEnabled) {
            config.sftp = {
                host: this.state.sftpHost.trim(),
                username: this.state.sftpUsername.trim()
            };
            if (this.state.sftpPort) {
                config.sftp.port = Number(this.state.sftpPort);
            }
            if (this.state.sftpRemotePath.trim()) {
                config.sftp.remotePath = this.state.sftpRemotePath.trim();
            }
            // left out when blank, which is what makes the API keep the stored credential
            if (this.state.sftpAuthMethod === 'key') {
                const privateKey = this.state.sftpPrivateKey.trim();
                if (privateKey.length) {
                    config.sftp.privateKey = privateKey;
                    // the API rejects a passphrase without the key it belongs to
                    const passphrase = this.state.sftpPassphrase.trim();
                    if (passphrase.length) {
                        config.sftp.passphrase = passphrase;
                    }
                }
            } else {
                const password = this.state.sftpPassword.trim();
                if (password.length) {
                    config.sftp.password = password;
                }
            }
        }
        return Object.keys(config).length ? config : undefined;
    }

    createSchedule() {
        let self = this;
        // no date range to shift, so none of the direct path's timezone offset arithmetic applies
        return GridDuck.createDataRequest({
            type: this.state.scheduleType,
            name: this.state.scheduleName,
            granularity: this.state.granularity !== 'custom' ? this.state.granularity : this.state.customGranularity,
            assetIds: this.state.selectedAssets.map(a => a.id),
            dataType: this.state.dataType,
            notificationConfig: this.notificationConfig()
        }).then(function (created) {
            // the signing secret is returned once and never again, so hold the modal open on a
            // final panel rather than closing straight away
            if (created && created.webhookSecret) {
                return self.setState({step: 4, webhookSecret: created.webhookSecret});
            }
            // a schedule is not in the archive, so there is nowhere useful to navigate to yet
            if (self.props.onsubmit) {
                self.props.onsubmit();
            }
        });
    }

    createDataRequest() {
        let self = this;
        let dateStart = new moment(this.state.dateRange.start).tz(this.state.selectedAssets[0].siteTimezone);
        let browserStart = new moment(this.state.dateRange.start);
        let offsetStart = 0;
        if (dateStart && browserStart) {
            offsetStart = (dateStart.utcOffset() - browserStart.utcOffset()) * 60 * 1000;
        }
        let dateEnd = new moment(this.state.dateRange.end).tz(this.state.selectedAssets[0].siteTimezone);
        let browserEnd = new moment(this.state.dateRange.end);
        let offsetEnd = 0;
        if (dateEnd && browserEnd) {
            offsetEnd = (dateEnd.utcOffset() - browserEnd.utcOffset()) * 60 * 1000;
        }


        return GridDuck.createDataRequest({
            granularity: this.state.granularity !== 'custom' ? this.state.granularity : this.state.customGranularity,
            assetIds: this.state.selectedAssets.map(a => a.id),
            startDate: moment(this.state.dateRange.start - offsetStart).unix(),
            endDate: moment(this.state.dateRange.end - offsetEnd).unix(),
            dataType: this.state.dataType
        }).then(function () {

            history.push("/data-download/archive");
            if (self.props.onsubmit) {

                self.props.onsubmit()
            }
        });
    }

    isValidDate() {
        return ((this.state.granularity && (this.state.granularity !== 'custom' || this.state.customGranularity)) && (this.state.dateRange));
    }

    canSubmit() {
        // switching selection type clears selectedAssets but leaves dateRange, which used to crash
        if (!this.state.selectedAssets || !this.state.selectedAssets.length) {
            return false;
        }
        if (!this.state.granularity || (this.state.granularity === 'custom' && !this.state.customGranularity)) {
            return false;
        }
        if (this.state.mode === 'schedule') {
            return !!(this.state.scheduleType && this.state.scheduleName && this.state.scheduleName.trim())
                && this.deliveryComplete();
        }
        return !!this.state.dateRange;
    }

    // Create can leave the secret blank because the create response carries the generated one back
    // once. An update response is discarded by the client, so an added webhook needs a typed secret.
    webhookSecretRequired() {
        return !!this.props.editing && this.state.webhookEnabled && !this.state.webhookSecretStored;
    }

    // The message to put under the field, or null when it is fine. Returning the text rather than a
    // boolean is what stops the submit button greying out with nothing said.
    webhookSecretError() {
        const secret = this.state.webhookSecretInput.trim();
        if (secret.length && secret.length < MIN_WEBHOOK_SECRET_LENGTH) {
            return 'Must be at least ' + MIN_WEBHOOK_SECRET_LENGTH + ' characters';
        }
        if (!secret.length && this.webhookSecretRequired()) {
            return 'Required when adding a webhook to an existing schedule';
        }
        return null;
    }

    // Unlike a webhook signing secret, an sftp credential can never be generated for the user, so
    // it is required whenever there is not already one stored — on create as well as on edit.
    sftpCredentialRequired() {
        return this.state.sftpEnabled && !this.state.sftpSecretStored;
    }

    sftpCredentialError() {
        const usingKey = this.state.sftpAuthMethod === 'key';
        const supplied = usingKey ? this.state.sftpPrivateKey.trim() : this.state.sftpPassword.trim();
        if (!supplied.length && this.sftpCredentialRequired()) {
            return usingKey ? 'Private key is required' : 'Password is required';
        }
        return null;
    }

    // an enabled channel with nothing in it would be rejected by the API, so block it here
    deliveryComplete() {
        if (this.state.emailEnabled) {
            const recipients = this.state.emailRecipients.split(',').map(r => r.trim()).filter(r => r.length);
            if (!recipients.length || recipients.some(r => r.indexOf('@') === -1)) {
                return false;
            }
        }
        if (this.state.webhookEnabled) {
            const url = this.state.webhookUrl.trim();
            if (url.indexOf('https://') !== 0) {
                return false;
            }
            if (this.webhookSecretError()) {
                return false;
            }
        }
        if (this.state.sftpEnabled) {
            if (!this.state.sftpHost.trim() || !this.state.sftpUsername.trim()) {
                return false;
            }
            if (this.sftpCredentialError()) {
                return false;
            }
        }
        return true;
    }

    setGranularity(e) {

        let val = e.target.value, format = 'DD/MM/yyyy HH:mm';
        if (val !== 'custom') {
            if (val === 60 * 60) {
                format = 'DD/MM/yyyy HH';
            } else if (val === 86400) {
                format = 'DD/MM/yyyy';
            }
        }

        this.setState({granularity: e.target.value, format: format})
    }

    selectionChanged(e) {
        let self = this;
        this.setState({selectionType: e.target.value, selectedAssets: [], filters: [], isEditing: true}, () => {
            if (this.props.inPage) this.props.onUpdate([], this.state, this.filteredAssetList)
        });
        if (e.target.value === 'individual') {
            this.setState({listSaved: false});
        }
        if (e.target.value === 'all') {
            self.setState({loading: true});
            GridDuck.getAssets({
                getAll: true,
                filters: this.props.onlyAllow
            }).then(function (assetList) {
                self.setState({
                    selectedAssets: assetList.list,
                    loading: false
                }, () => {
                    if (self.props.inPage) self.props.onUpdate(assetList.list, self.state, self.filteredAssetList);
                });
            });
        }
    }

    deleteFilter(filterIndex) {
        let filters = this.state.filters;
        let thisFilter = filters[filterIndex];
        let selectedAssets = this.state.selectedAssets ? this.state.selectedAssets : [];
        selectedAssets = selectedAssets.filter(a => a.siteId !== thisFilter.id);
        filters.splice(filterIndex, 1);
        this.filteredAssetList.splice(filterIndex, 1);
        this.setState({filters: filters, selectedAssets: selectedAssets}, () => {
            if (this.props.inPage) this.props.onUpdate(selectedAssets, this.state, this.filteredAssetList)
        });
        this.updateSelectedAssets();
    }

    filterUpdated(filterIndex, params) {
        let self = this;
        let filters = this.state.filters;

        let FFilters = this.props.onlyAllow ? this.props.onlyAllow : [];
        if (params.item) {
            let item = params.item;
            filters[filterIndex].id = item.id;
            self.setState({filters: filters, loading: true});

            //  //TODO Add in response of only site_ids and group_ids
            // if (this.props.inPage === 'report') {return self.setState({selectedAssets: FFilters.concat([{
            //          field: 'siteId',
            //          value: item.id
            //      }]), loading: false}, ()=> {if (self.props.inPage) self.props.onUpdate(validAssetsSoFar, self.state, self.filteredAssetList)});}

            switch (filters[filterIndex].type) {
                case 'site':
                    GridDuck.getAssets({
                        getAll: true,
                        filters: FFilters.concat([{
                            field: 'siteId',
                            value: item.id
                        }])
                    }).then(function (assetList) {
                        self.filteredAssetList[filterIndex] = assetList.list;
                        self.updateSelectedAssets();
                    });
                    break;
                case 'group':
                    GridDuck.getGroup(
                        {
                            id: item.id
                        })
                        .then(function (group) {
                            return GridDuck.getAssets({
                                filters: FFilters.concat([
                                    {
                                        field: 'id',
                                        value: group.assetIds
                                    }
                                ])
                            }).then(function (assetList) {
                                self.filteredAssetList[filterIndex] = assetList.list;
                                self.updateSelectedAssets();
                            });
                        });
                    break;
                case 'Site Group':
                    GridDuck.getSiteGroup(
                        {
                            id: item.id
                        })
                        .then(function (siteGroup) {
                            let siteAssetsPromises = [];
                            siteGroup.siteIds.forEach((sid) => {
                                siteAssetsPromises.push(GridDuck.getAssets({
                                    filters: FFilters.concat([{
                                        field: 'siteId',
                                        value: sid
                                    }])
                                }));
                            });
                            Promise.all(siteAssetsPromises).then((sats) => {
                                let allAssets = [];
                                sats.forEach((s) => {
                                    allAssets = allAssets.concat(s.list);
                                });
                                self.filteredAssetList[filterIndex] = allAssets;

                                self.updateSelectedAssets();
                            });
                        });
                    break;
                default:
                    break;
            }
        }
        if (params.logic) {
            self.setState({filterLogic: params.logic}, self.updateSelectedAssets);
        }
    }

    updateSelectedAssets() {
        let self = this;
        let logic = this.state.filterLogic;

        if (logic === 'OR') {
            let selectedAssets = [];
            this.filteredAssetList.forEach(function (assetList) {
                assetList.forEach(function (asset) {
                    if (!selectedAssets.find(a => a.id === asset.id)) {
                        selectedAssets.push(asset);
                    }
                })
            });
            this.setState({selectedAssets: selectedAssets, loading: false}, () => {
                if (this.props.inPage) this.props.onUpdate(selectedAssets, this.state, this.filteredAssetList)
            });
        } else if (logic === 'AND') {
            let validAssetsSoFar = [];
            if (this.filteredAssetList.length > 0) {
                validAssetsSoFar = this.filteredAssetList[0].slice();

                this.filteredAssetList.forEach(function (assetList) {
                    let toRemove = [];
                    validAssetsSoFar.forEach(function (validAssetSoFar) {
                        if (!assetList.find(a => a.id === validAssetSoFar.id)) {
                            toRemove.push(validAssetSoFar.id);
                        }
                    })

                    toRemove.forEach(function (assetIdToRemove) {
                        validAssetsSoFar.splice(validAssetsSoFar.indexOf(validAssetsSoFar.find(a => a.id === assetIdToRemove)), 1);
                    })
                });

                self.setState({selectedAssets: validAssetsSoFar, loading: false}, () => {
                    if (self.props.inPage) self.props.onUpdate(validAssetsSoFar, self.state, self.filteredAssetList)
                });
            }
        }
    }

    getList(type, searchTerm) {
        switch (type) {
            case 'site':
                return GridDuck.getSites({
                    filters: [{
                        field: 'search',
                        value: searchTerm
                    }]
                }).then(function (siteList) {
                    return Promise.resolve(
                        siteList.list.map(
                            s => ({
                                id: s.id,
                                title: s.name
                            })
                        )
                    )
                })
            case 'group':
                return GridDuck.getGroups({
                    filters: [{
                        field: 'search',
                        value: searchTerm
                    }]
                }).then(function (groupList) {
                    return Promise.resolve(
                        groupList.list.map(
                            s => ({
                                id: s.id,
                                title: s.name
                            })
                        )
                    )
                })
            case 'Site Group':
                return GridDuck.getSiteGroups({
                    filters: [{
                        field: 'search',
                        value: searchTerm
                    }]
                }).then(function (groupList) {
                    return Promise.resolve(
                        groupList.list.map(
                            s => ({
                                id: s.id,
                                title: s.name
                            })
                        )
                    )
                })
            default:
                break;
        }
        return Promise.resolve([
            {
                id: 1,
                title: searchTerm
            }
        ]);
    }

    componentDidMount() {
        if (!this.drawerFooter) {
            this.drawerFooter = document.getElementById('drawer-footer');

            this.setState({updateRef: 1});
        }
    }

    getFilters() {
        let self = this;
        let filters = [];
        if (this.state.filters) {
            let filterItems = [
                {
                    id: 'OR',
                    value: 'OR',
                    title: 'or'
                }];
            if (self.state.filters.filter(f => f.type === 'site').length < 2 && self.props.inPage !== 'report') {
                filterItems.push({
                    id: 'AND',
                    value: 'AND',
                    title: 'and'
                });
            }

            this.state.filters.forEach(function (filter, index) {
                filters.push(
                    <div className={"filter"} key={index + 'f'}>
                        {index !== 0 ? <div className={'and-or'}>
                            <Dropdown
                                key={index + 'dd'}
                                value={self.state.filterLogic}
                                name={'logic'}
                                nolabel='true'
                                fixeditems={filterItems}
                                onChange={ev => self.filterUpdated(index, {logic: ev.target.value})}
                                disabled={self.state.filters.filter(f => f.type === 'site').length > 1}
                            />
                        </div> : ''}
                        <GdAutocomplete
                            key={index + 'gda'}
                            async
                            autoFocus
                            placeholder={filter.type.replace(
                                /\w\S*/g,
                                function (txt) {
                                    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
                                }
                            ) + ' Name'}
                            value={filter.value}
                            onChange={(ev, item) => {
                                self.filterUpdated(index, {item: item});
                                filter.value = item ? item.title : '';
                            }}
                            getList={(searchTerm) => self.getList(filter.type, searchTerm)}
                            label={filter.name}
                        />
                        <Icon icon={"FaTimes"}
                              color={'gd-red'}
                              key={index + 'i'}
                              size={'14'}
                              onIconClick={() => self.deleteFilter(index)}
                        />

                    </div>
                )
            });
        }
        return filters;
    }

    getSelection() {
        let self = this;
        let filterTypes = [
            {
                icon: {
                    color: 'gd-grey',
                    name: 'FaCubes',
                    size: '12'
                },
                label: 'Device Group',
                onMenuItemClick: function () {
                    let filters = self.state.filters ? self.state.filters : [];
                    filters.push({
                        type: 'group',
                        id: ''
                    });
                    self.setState({
                        filters: filters
                    });
                }
            }
        ];
        if (!this.state.filters || this.state.filterLogic !== 'AND' || this.state.filters.filter(f => f.type === 'site').length === 0) {
            filterTypes.unshift(
                {
                    icon: {
                        color: 'gd-grey',
                        name: 'FaBuilding',
                        size: '12'
                    },
                    label: 'Site',
                    onMenuItemClick: function () {
                        let filters = self.state.filters ? self.state.filters : [];
                        filters.push({
                            type: 'site',
                            id: ''
                        });
                        self.setState({
                            filters: filters
                        });
                    }
                }
            )
        }
        if (!this.state.filters || this.state.filterLogic !== 'AND' || this.state.filters.filter(f => f.type === 'siteGroup').length === 0) {
            filterTypes.push(
                {
                    icon: {
                        color: 'gd-grey',
                        name: 'FaGlobeEurope',
                        size: '12'
                    },
                    label: 'Site Group',
                    onMenuItemClick: function () {
                        let filters = self.state.filters ? self.state.filters : [];
                        filters.push({
                            type: 'Site Group',
                            id: ''
                        });
                        self.setState({
                            filters: filters
                        });
                    }
                }
            )
        }
        return (
            <div className={"filtered-selection"}>
                <Menu menuHeader={{title: 'Filter type'}}
                      controlFromElement={<Button label={'Add filter'}/>} menuItems={filterTypes}/>
            </div>
        )
    }

    getIndividualList() {
        let activeList = this.props.ddState && this.props.ddState.selectedAssets ? this.props.ddState.selectedAssets : [];


        return Promise.resolve({list: [], total: 0});
    }

    getAvailableIndividualList(retrievalParams) {
        return GridDuck.getAssets(retrievalParams);
    }

    saveIndividualList(list) {

        let dataOptions = [];
        let self = this;
        let wantedStateOptions = [];
        self.state.selectedAssets.forEach(function (currentlySelectedItem) {
            wantedStateOptions.push(StateOptions.find(sO => sO.type === currentlySelectedItem.sku) || StateOptions.find(sO => sO.type === 'default'));
        });
        wantedStateOptions
            .forEach(function (stateOption) {
                stateOption.dataTypes
                    .forEach(function (dataType) {
                        if (!dataOptions.find(dO => dO.value === dataType.type)) {
                            let dataOption = {
                                value: dataType.type,
                                title: dataType.label + " (" + dataType.unit + ")",
                            };
                            if (~dataType.type.indexOf('SPEND')) {
                                dataOption.title = dataType.label;
                            }
                            dataOptions.push(dataOption);
                        }
                    });
            });
        let dataType = self.state.dataType;
        if (dataOptions.length > 0 && !dataOptions.find(dT => dT.value === dataType)) {
            if (dataOptions.find(dT => dT.value === 'DELIVERED')) {
                dataType = 'DELIVERED';
            } else {
                dataType = dataOptions[0].value;
            }
        }
        this.setState({
            dataType: dataType,
            selectedAssets: list,
            isEditing: false
        }, () => {
            if (this.props.inPage) this.props.onUpdate(list, this.state, this.filteredAssetList)
        });
        return Promise.resolve();
    }

    setCustomGranularity(e) {
        this.setState({
            customGranularity: e.target.value
        });
    }

    rangeUpdated(dateTime) {
        this.setState({
            dateRange: dateTime
        });
    }

    render() {
        let self = this;
        let dateSelection;
        let currentlySelected;
        let filterNotice;
        let loader;
        let createButton;
        if (!this.drawerFooter) {
            this.drawerFooter = document.getElementById('drawer-footer');

        }
        let optionVals = [{
            label: "Filtered (Site, Device Group, Site Group)",
            name: 'filtered',
            disabled: this.state.loading,
            value: 'filtered'
        }, {
            label: "Individually selected (Device name, Site name)",
            name: 'individual',
            disabled: this.state.loading,
            value: 'individual'
        }];

        if (this.props.inPage !== 'report') {

            optionVals.unshift({
                label: 'All',
                name: 'all',
                disabled: this.state.loading,
                value: 'all'
            });
        }
        if (this.state.loading) {
            loader = <GenericLoader text={"Fetching"} textLineTwo={"Devices..."}/>
        }
        if (this.state.filters && this.state.filters.filter(f => f.type === 'site').length > 1) {
            filterNotice = (
                <p className={"filter-notice"}>Can only filter by OR when including multiple sites as they
                    have a one-to-one relationship with devices.</p>
            )
        }
        if (this.state.filters && this.state.filterLogic !== 'OR') {
            filterNotice = (
                <p className={"filter-notice"}>To filter by multiple sites use OR</p>
            )
        }
        if (this.state.selectedAssets && this.state.selectedAssets.length > 0) {
            currentlySelected = (
                <p className={'no-sel'}>{this.state.selectedAssets.length} devices currently selected</p>
            )
            let customInput;
            if (this.state.granularity === 'custom') {
                customInput = [(
                    <Input top placeholder={"Granularity in seconds"} type={'number'} InputProps={{step: 1, min: 1}}
                           value={this.state.customGranularity}
                           label={"Seconds"} onChange={this.setCustomGranularity}/>
                )
                ]
                if (this.state.customGranularity) {
                    customInput.push(
                        <p className={'cust-gran'}>Aka: {moment.duration(this.state.customGranularity, 'seconds').humanize()}</p>
                    )
                }
            }

            let dataOptions = [];

            let wantedStateOptions = [];
            self.state.selectedAssets.forEach(function (currentlySelectedItem) {
                wantedStateOptions.push(StateOptions.find(sO => sO.type === currentlySelectedItem.sku) || StateOptions.find(sO => sO.type === 'default'));
            });
            wantedStateOptions
                .forEach(function (stateOption) {
                    stateOption.dataTypes
                        .forEach(function (dataType) {
                            if (!dataOptions.find(dO => dO.value === dataType.type)) {
                                let dataOption = {
                                    value: dataType.type,
                                    title: dataType.label + " (" + dataType.unit + ")",
                                };
                                if (~dataType.type.indexOf('SPEND')) {
                                    dataOption.title = dataType.label;
                                }
                                dataOptions.push(dataOption);
                            }
                        });
                });

            let dateSelectValue = this.state.dateRange && this.state.dateRange.start && this.state.dateRange.end ? [new Date(this.state.dateRange.start), new Date(this.state.dateRange.end)] : [];

            // a schedule derives its own window, so it asks for a cadence instead of a date range
            let periodSelection = this.state.mode === 'schedule' ? (
                <div>
                    <p className={"title-row"}>
                        Schedule
                    </p>
                    <div className={'row'} style={{marginBottom: '15px'}}>
                        <Input required
                               name={'scheduleName'}
                               label={'Schedule name'}
                               value={this.state.scheduleName}
                               onChange={(e) => this.setState({scheduleName: e.target.value})}/>
                    </div>
                    <div className={'row'} style={{marginBottom: '15px', alignItems: 'center'}}>
                        <Dropdown label={'Frequency'}
                                  onChange={(e) => this.setState({scheduleType: e.target.value})}
                                  fixeditems={[
                                      {
                                          id: 'schedule_weekly',
                                          value: 'schedule_weekly',
                                          title: 'Weekly'
                                      },
                                      {
                                          id: 'schedule_monthly',
                                          value: 'schedule_monthly',
                                          title: 'Monthly'
                                      },
                                  ]}
                                  value={this.state.scheduleType}/>
                        <p className={'cust-gran'}>
                            {this.state.scheduleType === 'schedule_monthly'
                                ? 'Runs on the 1st, covering the previous month'
                                : 'Runs every Monday, covering the previous week'}
                        </p>
                    </div>
                </div>
            ) : (
                <div>
                    <p className={"title-row"}>
                        Date Range
                    </p>
                    <div className={'row'} style={{marginBottom: '15px'}}>
                        <DateTimeRangePicker
                            label={'Start / End Date'}
                            pastOnly
                            cleanable={false} key={'selector'} placement={'auto'} onChange={this.rangeUpdated}
                            nolabel={true} value={dateSelectValue}/>
                    </div>
                </div>
            );

            dateSelection = (
                <div>
                    {periodSelection}
                    <div className={'row'} style={{marginBottom: '15px', alignItems: 'center'}}>
                        <Dropdown label={'Granularity'} onChange={this.setGranularity}
                                  fixeditems={[
                                      {
                                          id: 'custom',
                                          value: 'custom',
                                          title: 'Custom'
                                      },
                                      {
                                          id: 60,
                                          value: 60,
                                          title: 'Per minute'
                                      },
                                      {
                                          id: 3600,
                                          value: 3600,
                                          title: 'Per hour'
                                      },
                                      {
                                          id: 86400,
                                          value: 86400,
                                          title: 'Per day'
                                      },
                                      {
                                          id: 604800,
                                          value: 604800,
                                          title: 'Per week'
                                      },
                                  ]}
                                  value={this.state.granularity}/>
                        {customInput}
                    </div>
                    <div className={'row'} style={{marginBottom: '15px', alignItems: 'center'}}>
                        <Dropdown label={'Data Type'}
                                  onChange={(e) => this.setState({dataType: e.target.value})}
                                  fixeditems={dataOptions}
                                  value={this.state.dataType}/>
                    </div>
                </div>
            );
        }

        let selectionComponent;
        let filters;
        switch (this.state.selectionType) {
            case 'filtered':
                selectionComponent = this.getSelection();
                filters = this.getFilters();
                break;
            case 'individual':
                if (!this.state.listSaved) {
                    selectionComponent = (
                        <EditableList
                            isEditing={true}
                            noCancel={true}
                            noSave={true}
                            columns={[
                                {
                                    component: StandardListCell,
                                    bold: true,
                                    sortable: true,
                                    title: 'Name',
                                    field: 'name'
                                },
                                {
                                    component: StandardListCell,
                                    sortable: false,
                                    title: 'Site Name',
                                    field: 'siteName'
                                },
                            ]}
                            editingColumns={[
                                {component: StandardListCell, title: 'Site Name', field: 'siteName'},
                            ]}
                            getActiveList={this.getIndividualList}
                            getAvailableList={this.getAvailableIndividualList}
                            onChange={this.saveIndividualList}
                            availableFilters={this.props.onlyAllow}
                        />
                    )
                } else {
                    // the count pill joins the button's row here instead of rendering on its own below
                    selectionComponent = (
                        <div className={'selection-row'}>
                            <Button label={"Edit selection"} color={'gd-grey'}
                                    onClick={() => this.setState({listSaved: false})}/>
                            {currentlySelected}
                        </div>
                    )
                    currentlySelected = null;
                }
                break;
            default:
                break;
        }

        // step 1 asks how the data should be delivered; everything else stays on step 2 as before
        let modeSelection = (
            <div className={'inner-body'}>
                <p className={"title-row"}>
                    How do you want this data?
                </p>
                <div className={'row'} style={{marginBottom: '10px'}}>
                    <OptionCard onClick={() => this.setMode('direct')}
                                selected={this.state.mode === 'direct'}
                                description={'A one-off export for a date range you choose'}
                                color={'gd-grey'} icon={'FaFileDownload'} name={'Direct download'}/>
                </div>
                <div className={'row'} style={{marginBottom: '15px'}}>
                    <OptionCard onClick={() => this.setMode('schedule')}
                                selected={this.state.mode === 'schedule'}
                                description={'A recurring export that runs automatically and covers the period that just closed'}
                                color={'gd-grey'} icon={'FaHistory'} name={'Schedule'}/>
                </div>
            </div>
        );

        // only schedules have anything to deliver, so direct downloads finish on step 2
        const webhookSecretError = this.webhookSecretError();
        const sftpCredentialError = this.sftpCredentialError();
        let deliverySelection = (
            <div className={'inner-body'}>
                <p className={"title-row"}>
                    Delivery
                </p>
                <p className={'help-row'}>
                    Optional. Each completed run can be emailed, posted to your endpoint, and/or
                    uploaded to your SFTP server.
                </p>
                <div className={'row'} style={{marginBottom: '10px'}}>
                    <GdCheckbox checked={this.state.emailEnabled} name={'emailEnabled'}
                                label={'Email a download link'}
                                onChange={(e) => this.setState({emailEnabled: e.target.checked})}/>
                </div>
                {this.state.emailEnabled ? <div className={'row'} style={{marginBottom: '15px'}}>
                    <Input required
                           name={'emailRecipients'}
                           label={'Recipients (comma separated)'}
                           value={this.state.emailRecipients}
                           onChange={(e) => this.setState({emailRecipients: e.target.value})}/>
                </div> : null}
                <div className={'row'} style={{marginBottom: '10px'}}>
                    <GdCheckbox checked={this.state.webhookEnabled} name={'webhookEnabled'}
                                label={'POST to a webhook'}
                                onChange={(e) => this.setState({webhookEnabled: e.target.checked})}/>
                </div>
                {this.state.webhookEnabled ? <div>
                    <div className={'row'} style={{marginBottom: '10px'}}>
                        <Input required
                               name={'webhookUrl'}
                               label={'Webhook URL (https)'}
                               value={this.state.webhookUrl}
                               onChange={(e) => this.setState({webhookUrl: e.target.value})}/>
                    </div>
                    <div className={'row'} style={{marginBottom: '5px'}}>
                        <Input name={'webhookSecretInput'}
                               required={this.webhookSecretRequired()}
                               error={!!webhookSecretError}
                               helperText={webhookSecretError || ''}
                               label={this.webhookSecretRequired() ? 'Signing secret' : 'Signing secret (optional)'}
                               value={this.state.webhookSecretInput}
                               onChange={(e) => this.setState({webhookSecretInput: e.target.value})}/>
                    </div>
                    <p className={'cust-gran'}>
                        Signed with HMAC-SHA256 in an X-GridDuck-Signature header.
                        {this.props.editing
                            ? (this.state.webhookSecretStored
                                ? ' Leave blank to keep the current secret, or enter a new one to replace it.'
                                : ' Enter the secret your receiver expects. One cannot be generated for you here.')
                            : ' Enter your own secret to match an existing receiver, or leave it blank and one will be generated and shown to you once.'}
                    </p>
                </div> : null}
                <div className={'row'} style={{marginBottom: '10px'}}>
                    <GdCheckbox checked={this.state.sftpEnabled} name={'sftpEnabled'}
                                label={'Upload to an SFTP server'}
                                onChange={(e) => this.setState({sftpEnabled: e.target.checked})}/>
                </div>
                {this.state.sftpEnabled ? <div>
                    <div className={'row'} style={{marginBottom: '10px'}}>
                        <Input required
                               name={'sftpHost'}
                               label={'Host'}
                               value={this.state.sftpHost}
                               onChange={(e) => this.setState({sftpHost: e.target.value})}/>
                        <Input name={'sftpPort'}
                               label={'Port (default 22)'}
                               value={this.state.sftpPort}
                               onChange={(e) => this.setState({sftpPort: e.target.value})}/>
                    </div>
                    <div className={'row'} style={{marginBottom: '10px'}}>
                        <Input required
                               name={'sftpUsername'}
                               label={'Username'}
                               value={this.state.sftpUsername}
                               onChange={(e) => this.setState({sftpUsername: e.target.value})}/>
                        <Input name={'sftpRemotePath'}
                               label={'Remote directory (optional)'}
                               value={this.state.sftpRemotePath}
                               onChange={(e) => this.setState({sftpRemotePath: e.target.value})}/>
                    </div>
                    <div className={'row'} style={{marginBottom: '10px'}}>
                        <GdRadio onChange={(e) => this.setState({sftpAuthMethod: e.target.value})}
                                 value={this.state.sftpAuthMethod} row
                                 options={[
                                     {label: 'Password', name: 'password', value: 'password'},
                                     {label: 'Private key', name: 'key', value: 'key'}
                                 ]}/>
                    </div>
                    {this.state.sftpAuthMethod === 'key' ? <div>
                        <div className={'row'} style={{marginBottom: '5px'}}>
                            <Input multiline
                                   name={'sftpPrivateKey'}
                                   required={this.sftpCredentialRequired()}
                                   error={!!sftpCredentialError}
                                   helperText={sftpCredentialError || ''}
                                   label={'Private key'}
                                   value={this.state.sftpPrivateKey}
                                   onChange={(e) => this.setState({sftpPrivateKey: e.target.value})}/>
                        </div>
                        <div className={'row'} style={{marginBottom: '5px'}}>
                            <Input name={'sftpPassphrase'}
                                   label={'Key passphrase (optional)'}
                                   value={this.state.sftpPassphrase}
                                   onChange={(e) => this.setState({sftpPassphrase: e.target.value})}/>
                        </div>
                    </div> : <div className={'row'} style={{marginBottom: '5px'}}>
                        <Input name={'sftpPassword'}
                               type={'password'}
                               required={this.sftpCredentialRequired()}
                               error={!!sftpCredentialError}
                               helperText={sftpCredentialError || ''}
                               label={'Password'}
                               value={this.state.sftpPassword}
                               onChange={(e) => this.setState({sftpPassword: e.target.value})}/>
                    </div>}
                    <p className={'cust-gran'}>
                        Each completed run is uploaded to this server. Credentials are stored
                        encrypted and never shown again.
                        {this.state.sftpSecretStored
                            ? ' Leave the credential blank to keep the current one.'
                            : ''}
                    </p>
                </div> : null}
            </div>
        );

        let secretPanel = (
            <div className={'inner-body'}>
                <p className={"title-row"}>
                    Webhook signing secret
                </p>
                <p className={'help-row'}>
                    Copy this now — it is not shown again. Use it to verify the
                    X-GridDuck-Signature header on each delivery.
                </p>
                <div className={'row'} style={{marginBottom: '10px'}}>
                    <Input readOnly name={'webhookSecret'} label={'Signing secret'}
                           value={this.state.webhookSecret || ''}/>
                </div>
                <div className={'row'} style={{marginBottom: '15px'}}>
                    <Button label={this.state.secretCopied ? "Copied" : "Copy to clipboard"}
                            color={this.state.secretCopied ? 'gd-green' : 'gd-blue'}
                            additionalclasses={'sm'} onClick={this.copySecret}/>
                </div>
            </div>
        );

        const lastConfigStep = this.state.mode === 'schedule' ? 3 : 2;
        // editing starts on the device step, so there is no mode picker to go back to
        const firstStep = this.props.editing ? 2 : 1;
        const submitLabel = this.props.editing
            ? "Save Changes"
            : (this.state.mode === 'schedule' ? "Create Schedule" : "Create Data Request");
        let footerButtons;
        if (this.state.step === 4) {
            footerButtons = (
                <Button color={'gd-green'} label={"Done"} additionalclasses={'md'}
                        onClick={() => this.props.onsubmit ? this.props.onsubmit() : null}/>
            );
        } else if (this.state.step === 1) {
            footerButtons = (
                <Button label={"Next"}
                        disabled={!this.state.mode}
                        additionalclasses={'md'}
                        onClick={() => this.setState({step: 2})}/>
            );
        } else {
            footerButtons = (
                <React.Fragment>
                    {this.state.step === lastConfigStep ?
                        <Button color={'gd-green'}
                                label={submitLabel}
                                disabled={!this.canSubmit()}
                                additionalclasses={'md'}
                                onClick={this.submit}
                                progressRes={true}/> :
                        <Button label={"Next"}
                                disabled={!this.canSubmit()}
                                additionalclasses={'md'}
                                onClick={() => this.setState({step: this.state.step + 1})}/>}
                    {this.state.step > firstStep ?
                        <div style={{marginRight: '10px'}}>
                            <Button label={"Back"} color={'gd-grey'} additionalclasses={'md'}
                                    onClick={() => this.setState({step: this.state.step - 1})}/>
                        </div> : null}
                </React.Fragment>
            );
        }

        return (
            !this.props.inPage && this.state ?
                <div className={"inner-rule DataDownload"}>
                    <div className={"details"}>
                        <div className={'details-inner'}>
                            {this.state.step === 1 ? modeSelection : null}
                            {this.state.step === 2 ? <div className={'inner-body'}>
                                <p className={"title-row"}>
                                    Devices
                                </p>


                                <GdRadio
                                    onChange={this.selectionChanged}
                                    value={this.state.selectionType} row options={optionVals}/>
                                {loader}
                                {filterNotice}
                                {filters}
                                {selectionComponent}
                                {currentlySelected}
                                {dateSelection}
                            </div> : null}
                            {this.state.step === 3 ? deliverySelection : null}
                            {this.state.step === 4 ? secretPanel : null}
                            {this.drawerFooter ? createPortal(<div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row-reverse',
                                    flex: '1 1'
                                }}>{footerButtons}</div>, this.drawerFooter) : null}
                        </div>
                    </div>
                </div> :
                <div className={'DataDownload'}>
                    <Card>
                        {this.state ? <div style={{width: '100%'}}>
                            <GdRadio
                                onChange={this.selectionChanged}
                                value={this.state.selectionType} row options={optionVals}/>

                            {loader}
                            {filterNotice}
                            {filters}
                            {selectionComponent}
                            {currentlySelected}
                        </div> : ''}
                    </Card>
                </div>
        )
    }
}

export default CreateDataDownload;
